const get = require('lodash/get')
const {listBatches, purgeCache} = require('./api/batch-list')
const getBatch = require('./api/batch-get')
const onBatchListUpdated = require('./api/batch-on-list-updated')
const getOrList = require('./api/get-or-list-batch')
const upsert = require('./api/upsert')
const listManufacturers = require('./api/batch-list-manufacturers')
const newBatch = require('./api/batch-new')
const getProductNonExpiredBatches = require('./api/get-product-non-expired-batches')
const getExpiredBatchIds = require('./api/get-expired-batch-ids')
const missingBatches = require('./api/batch-missing')
const findBatchesByBatchNos = require('./api/batch-find-by-batch-no')
const { wrapEntityApi } = require('../utils/wrap-api')
const BatchPgAdapter = require('./data-access/batch-pg-adapter')

const rawMethods = {
  new: newBatch,
  list: listBatches,
  _purgeCache: purgeCache,
  listManufacturers: listManufacturers,
  onListUpdated: onBatchListUpdated,
  missing: missingBatches,
  get: getBatch,
  findByBatchNos: findBatchesByBatchNos
}

class BatchApi {
  // `pgConnection` is only ever passed from inside agave (see api/index.js) —
  // that's what makes `this.pgAdapter` the routing signal for the PG-backed
  // reads. Every other caller (browser, lambdas, data-ops, the sync jobs)
  // constructs BatchApi without one, so `list`/`get`/`missing`/
  // `getNonExpired` above are completely unaffected by this change. Callers
  // (agave routes) use `batch.pgAdapter.getByIds`/`getOne`/etc directly
  // rather than wrapper methods here — calling `batch.pgAdapter` without a
  // pgConnection throws on its own.
  constructor (state, mainApi, agaveAdapter, pgConnection, logger) {
    const apiMethods = wrapEntityApi(rawMethods, state)
    Object.assign(this, apiMethods)
    this.mainApi = mainApi
    this.state = state
    this.agaveAdapter = agaveAdapter

    if (pgConnection) {
      const username = get(state, 'user.name')
      this.pgAdapter = new BatchPgAdapter(pgConnection, username, logger)
    }
  }

  // Fetch batches from the shared remote DB via agave. Used as a fallback when
  // the local (per-user) DB doesn't hold the batch docs — e.g. retailer
  // delivery statements, where batches are never synced into the user DB.
  // Mirrors product.getProductsViaIds.
  getBatchesViaIds (ids) {
    if (this.agaveAdapter) {
      return this.agaveAdapter.create('batch/by-ids', { ids })
    }
    // Fallback to local pouch
    return listBatches(this.state, { ids })
  }

  // Last-resort fallback for when a batch can't be found by its compound
  // _id — see findBatchesByBatchNos for why this is keyed on batchNo alone
  // and reads CouchDB directly rather than Postgres.
  getBatchesByBatchNosViaAgave (batchNos) {
    if (this.agaveAdapter) {
      return this.agaveAdapter.create('batch/by-batch-no', { batchNos })
    }
    // Fallback to local pouch
    return findBatchesByBatchNos(this.state, batchNos)
  }

  // Called by the NHLMIS (PSM) REST API
  getOrList (queryParams, alias) {
    return getOrList(
      this.mainApi,
      { ...queryParams, userPrograms: get(this.state, 'user.programs', []) },
      alias
    )
  }

  // Called by the NHLMIS (PSM) REST API
  upsert (batch, alias, updatedAliasId) {
    return upsert(this.mainApi, batch, alias, updatedAliasId)
  }

  getNonExpired (productIds) {
    return getProductNonExpiredBatches(this.mainApi.state, productIds)
  }

  getExpiredBatchIds (batchIds, date) {
    return getExpiredBatchIds(this.mainApi.state, batchIds, date)
  }
}

module.exports = rawMethods
module.exports.BatchApi = BatchApi
